import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import "./UserForm.css";
import "./EquipmentForm.css";
import "./AmcSchedules.css";
import "../SafetyObservation/safetyObservation.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import ObservationFormFields from "../SafetyObservation/ObservationFormFields";
import {
  buildObservationFormData,
  fetchDepartmentOptions,
  normalizeDateToYyyyMmDd,
  parseApiError,
} from "../SafetyObservation/observationUtils";

const getTodayDateValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const INITIAL_FORM_DATA = {
  title: "",
  type: "",
  severity: "",
  description: "",
  location: "",
  observer_name: "",
  department_id: "",
  observed_date: getTodayDateValue(),
};

function SafetyObservationForm({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isDocumentPopupOpen, setIsDocumentPopupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.title.trim()) validationErrors.title = "Title is required.";
    if (!formData.type) validationErrors.type = "Type is required.";
    if (!formData.severity) validationErrors.severity = "Severity is required.";
    if (!formData.observed_date)
      validationErrors.observed_date = "Observed date is required.";

    return validationErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setFormData((prevState) => ({
      ...prevState,
      department_id: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prevState) => ({
      ...prevState,
      department_id: "",
    }));
  };

  const handleCancelClick = () => {
    navigate("/safety-observations");
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setDocuments((prevState) => [...prevState, ...selectedFiles]);
    setErrors((prevState) => ({
      ...prevState,
      documents: "",
    }));
    event.target.value = "";
  };

  const handleDeleteDocument = (index) => {
    setDocuments((prevState) =>
      prevState.filter((_, currentIndex) => currentIndex !== index),
    );
  };

  const handleSaveClick = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildObservationFormData(
        {
          ...formData,
          department_id: formData.department_id || "",
          observed_date: normalizeDateToYyyyMmDd(formData.observed_date) || "",
          description: formData.description || "",
          location: formData.location || "",
        },
        documents,
      );

      const response = await axios.post(
        `${API_URL}/api/safety/observations`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      showSnackbar(
        response.data?.message || "Observation created successfully",
      );
      window.setTimeout(() => {
        navigate("/safety-observations");
      }, 900);
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to create observation"),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const options = await fetchDepartmentOptions();
        setDepartments(options);
      } catch (error) {
        showSnackbar(
          parseApiError(error, "Failed to fetch departments"),
          "error",
        );
      }
    };

    loadDepartments();
  }, []);

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content observation-form-wrapper">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New Observation</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail">
              <ObservationFormFields
                formData={formData}
                errors={errors}
                departments={departments}
                selectedDepartment={selectedDepartment}
                onInputChange={handleInputChange}
                onDepartmentChange={handleDepartmentChange}
                fileInputRef={fileInputRef}
                documents={documents}
                isDocumentPopupOpen={isDocumentPopupOpen}
                onUploadClick={handleUploadClick}
                onFileInputChange={handleFileInputChange}
                onToggleDocumentPopup={() =>
                  setIsDocumentPopupOpen((prevState) => !prevState)
                }
                onCloseDocumentPopup={() => setIsDocumentPopupOpen(false)}
                onDeleteDocument={handleDeleteDocument}
              />
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyObservationForm;
